import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, Scrolling, Grouping, GroupPanel, Selection, Editing } from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import Form, { SimpleItem } from 'devextreme-react/form';
import { Popup } from 'devextreme-react/popup';
import { FileUploader } from 'devextreme-react/file-uploader';
import { CheckBox } from 'devextreme-react/check-box';
import { ScrollView } from 'devextreme-react/scroll-view';
import { SplitPanel } from '../../utils/split-panel/SplitPanel';
import { LoadPanel } from 'devextreme-react/load-panel';
import notify from 'devextreme/ui/notify';
import competitionEntityApi from '../../../api/competitionEntityApi';
import { analyzeOfficialsCsv, importOfficialsCsv } from '../../../utils/competitionImportExportApi';
import classNames from 'classnames';

const OfficialsLeftPane = React.memo(React.forwardRef(function OfficialsLeftPane({ dataSource, onSelect, onSaving, isSaving, sessionLookup, canEdit }, ref) {
    const gridRef = useRef(null);
    React.useImperativeHandle(ref, () => ({
        clearSelection: () => gridRef.current?.instance?.clearSelection(),
    }), []);
    return (
        <div className='dx-card' style={{ padding: 12, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
            <DataGrid
                ref={gridRef}
                className='grid theme-dependent'
                dataSource={dataSource}
                keyExpr='officialId'
                columnAutoWidth
                rowAlternationEnabled
                hoverStateEnabled
                showBorders={false}
                repaintChangesOnly
                onSelectionChanged={(e) => { const row = e.selectedRowsData?.[0]; onSelect(row ? row.officialId : null); }}
                onSaving={onSaving}
                loadPanel={{ enabled: isSaving, text: 'Speichern...' }}
                style={{ flex: 1, minHeight: 0 }}
            >
                <GroupPanel visible />
                <Grouping autoExpandAll={false} />
                <Selection mode='single' />
                <Column dataField='sessionId' caption='Abschnitt' width={120} lookup={{ dataSource: sessionLookup, valueExpr: 'id', displayExpr: 'name' }} groupIndex={0} />
                <Column dataField='officialTitle' caption='Funktion' width={160} />
                <Column dataField='officialName' caption='Name' />
                <Column dataField='officeClub' caption='Verein' width={130} />
                <Editing mode='popup' allowAdding={canEdit} useIcons />
                <Scrolling mode='virtual' />
            </DataGrid>
        </div>
    );
}));

export function CompetitionOfficials({ competitionId, sessions, accessToken, onEntityChange, onImportSuccess, dataSource }) {
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [importPopupVisible, setImportPopupVisible] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [importLoadingText, setImportLoadingText] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const canEdit = !!accessToken;

  const storeItems = dataSource?.store?._array || [];
  const storeRef = useRef(storeItems);
  useEffect(() => { storeRef.current = dataSource?.store?._array || []; });

  const sessionLookup = useMemo(() => (
    sessions?.map((session) => ({ id: session.sessionId, name: session.description || `Abschnitt ${session.number}` })) || []
  ), [sessions]);

  const sessionLabelByNumber = useMemo(() => {
    const map = new Map();
    (sessions || []).forEach((session) => {
      const number = session.number != null ? Number(session.number) : null;
      if (number != null) {
        map.set(number, session.description || `Abschnitt ${number}`);
      }
    });
    return map;
  }, [sessions]);

  useEffect(() => {
    if (!selectedId) { setFormData(null); setIsDirty(false); return; }
    const item = storeRef.current.find(o => o.officialId === selectedId);
    setFormData(item ? { ...item } : null);
    setIsDirty(false);
    setIsEditing(false);
  }, [selectedId]);

  const handleSelect = useCallback((id) => setSelectedId(id), []);
  const leftPaneRef = useRef(null);

  const updateField = useCallback((field, value) => {
    setFormData(prev => prev ? { ...prev, [field]: value } : prev);
    setIsDirty(true);
  }, []);

  const handleSaveForm = useCallback(async () => {
    if (!formData || !accessToken || !selectedId) return;
    setIsSaving(true);
    try {
      const current = storeRef.current.find(o => o.officialId === selectedId) || {};
      const saved = await competitionEntityApi.officials.update(competitionId, selectedId, { ...current, ...formData, officialId: selectedId, competitionId }, accessToken);
      onEntityChange?.('Official', 'update', saved);
      setIsDirty(false);
      setIsEditing(false);
      notify('Gespeichert', 'success', 2000);
    } catch (err) {
      notify(err?.response?.data || err?.message || 'Fehler beim Speichern', 'error', 4000);
    } finally {
      setIsSaving(false);
    }
  }, [accessToken, competitionId, formData, onEntityChange, selectedId]);

  const handleCancelForm = useCallback(() => {
    const item = storeRef.current.find(o => o.officialId === selectedId);
    setFormData(item ? { ...item } : null);
    setIsDirty(false);
    setIsEditing(false);
  }, [selectedId]);

  const handleDeleteForm = useCallback(async () => {
    if (!accessToken || !selectedId) return;
    if (!window.confirm('Kampfrichter wirklich löschen?')) return;
    setIsSaving(true);
    try {
      await competitionEntityApi.officials.remove(competitionId, selectedId, accessToken);
      onEntityChange?.('Official', 'delete', { officialId: selectedId });
      setSelectedId(null);
      leftPaneRef.current?.clearSelection();
      notify('Gelöscht', 'success', 2000);
    } catch (err) {
      notify(err?.response?.data || err?.message || 'Fehler beim Löschen', 'error', 4000);
    } finally {
      setIsSaving(false);
    }
  }, [accessToken, competitionId, onEntityChange, selectedId]);

  const handleGridSaving = useCallback(async (e) => {
    if (!e.changes?.length) return;
    if (!accessToken) { notify('Kein Zugriffstoken verfügbar.', 'warning', 3000); e.cancel = true; return; }
    e.cancel = true;
    setIsSaving(true);
    try {
      for (const change of e.changes) {
        if (change.type === 'insert') {
          const payload = { ...change.data, competitionId };
          const saved = await competitionEntityApi.officials.insert(competitionId, payload, accessToken);
          onEntityChange?.('Official', 'insert', saved);
        }
      }
      e.component.cancelEditData();
    } catch (err) {
      notify(err?.response?.data || err?.message || 'Fehler beim Speichern', 'error', 4000);
    } finally {
      setIsSaving(false);
    }
  }, [accessToken, competitionId, onEntityChange]);

  const handleImportPopupClose = useCallback(() => {
    setImportPopupVisible(false);
    setAnalysis(null);
    setSelectedFile(null);
    setImportFileName('');
    setReplaceExisting(true);
  }, []);

  const handleCsvSelected = useCallback(async (e) => {
    const file = (e?.value && e.value[0]) || null;
    if (!file) {
      setAnalysis(null);
      setImportFileName('');
      return;
    }

    setSelectedFile(file);
    setImportFileName(file.name);

    try {
      const serverResult = await analyzeOfficialsCsv(file, competitionId, accessToken);
      const normalized = {
        success: serverResult.success ?? serverResult.Success,
        message: serverResult.message ?? serverResult.Message,
        totalEntries: serverResult.totalEntries ?? serverResult.TotalEntries ?? 0,
        sectionNumbers: serverResult.sectionNumbers ?? serverResult.SectionNumbers ?? [],
        missingSectionNumbers: serverResult.missingSectionNumbers ?? serverResult.MissingSectionNumbers ?? [],
        warnings: serverResult.warnings ?? serverResult.Warnings ?? [],
        fileId: serverResult.fileId ?? serverResult.FileId ?? null,
        preview: serverResult.preview ?? serverResult.Preview ?? []
      };

      setAnalysis(normalized);

      if (!normalized.success) {
        notify(normalized.message || 'Analyse fehlgeschlagen', 'error', 5000);
        return;
      }

      if ((normalized.totalEntries || 0) === 0) {
        notify('Die Datei enthaelt keine importierbaren Kampfrichterzeilen.', 'warning', 4000);
      }

      if (normalized.missingSectionNumbers && normalized.missingSectionNumbers.length > 0) {
        notify(`Fuer ${normalized.missingSectionNumbers.length} Abschnitt(e) fehlen Session-Nummern im Wettkampf.`, 'warning', 5000);
      }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Analyse fehlgeschlagen';
      notify(message, 'error', 5000);
      setAnalysis(null);
      setImportFileName('');
    }
  }, [accessToken, competitionId]);

  const handleImportCsv = useCallback(async () => {
    if (!accessToken) {
      notify('Kein Zugriffstoken verfügbar. Bitte erneut anmelden.', 'warning', 3000);
      return;
    }

    if (!analysis || !analysis.success) {
      notify('Bitte zuerst eine CSV analysieren.', 'warning', 3000);
      return;
    }

    const unresolved = analysis.missingSectionNumbers || [];
    if (unresolved.length > 0) {
      notify('Es fehlen Abschnitte im Wettkampf. Import abgebrochen.', 'error', 4000);
      return;
    }

    setIsImporting(true);
    setImportLoadingText('Kampfrichter werden importiert...');

    try {
      const settings = {
        competitionId,
        replaceExisting,
        fileId: analysis.fileId || null
      };

      const response = await importOfficialsCsv(settings, analysis.fileId ? null : selectedFile, accessToken);

      if (response?.success) {
        const count = response.importedOfficialsCount || 0;
        notify(`CSV Import abgeschlossen (${count} Eintraege).`, 'success', 4000);
        handleImportPopupClose();
        onEntityChange?.('Official', 'refresh', {});
        onImportSuccess?.();
      } else {
        const message = response?.message || 'Import fehlgeschlagen';
        notify(message, 'error', 5000);
        if (response?.errors?.length) {
          notify(response.errors.join('; '), 'error', 5000);
        }
      }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Import fehlgeschlagen';
      notify(message, 'error', 5000);
    } finally {
      setIsImporting(false);
      setImportLoadingText('');
    }
  }, [accessToken, analysis, competitionId, handleImportPopupClose, onEntityChange, onImportSuccess, replaceExisting, selectedFile]);

  const renderFormToolbar = useCallback((title) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
      <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
      {canEdit && (
        <div style={{ display: 'flex', gap: 6 }}>
          {isEditing ? (
            <>
              <Button text='Speichern' type='default' stylingMode='contained' disabled={isSaving} onClick={handleSaveForm} />
              <Button text='Abbrechen' stylingMode='outlined' disabled={isSaving} onClick={handleCancelForm} />
              <Button icon='trash' stylingMode='outlined' hint='Löschen' disabled={isSaving} onClick={handleDeleteForm} />
            </>
          ) : (
            <>
              <Button text='Bearbeiten' icon='edit' stylingMode='outlined' disabled={!formData} onClick={() => setIsEditing(true)} />
              <Button icon='trash' stylingMode='outlined' hint='Löschen' disabled={!formData || isSaving} onClick={handleDeleteForm} />
            </>
          )}
        </div>
      )}
    </div>
  ), [canEdit, formData, handleCancelForm, handleDeleteForm, handleSaveForm, isEditing, isSaving]);

  const renderDetail = () => {
    if (!formData) {
      return (
        <div className='dx-card' style={{ padding: 20, color: '#888' }}>
          Bitte einen Kampfrichter aus der Liste links auswählen.
        </div>
      );
    }
    const f = formData;
    return (
      <div className={classNames('dx-card', 'plain-styled-form', { 'view-mode': !isEditing })} style={{ padding: 16 }}>
        {renderFormToolbar(f.officialName || 'Kampfrichter')}
        <Form formData={f} labelMode='floating' colCount={4} readOnly={!isEditing} onFieldDataChanged={(e) => updateField(e.dataField, e.value)}>
          <SimpleItem dataField='officialName' label={{ text: 'Name' }} editorOptions={{ stylingMode: 'filled' }} />
          <SimpleItem dataField='officialTitle' label={{ text: 'Funktion' }} editorOptions={{ stylingMode: 'filled' }} />
          <SimpleItem dataField='officialGroup' label={{ text: 'Gruppe' }} editorOptions={{ stylingMode: 'filled' }} />
          <SimpleItem dataField='officeClub' label={{ text: 'Verein' }} editorOptions={{ stylingMode: 'filled' }} />
          <SimpleItem dataField='sessionId' label={{ text: 'Abschnitt' }} editorType='dxSelectBox' editorOptions={{ dataSource: sessionLookup, valueExpr: 'id', displayExpr: 'name', stylingMode: 'filled' }} />
          <SimpleItem dataField='sortIndex' label={{ text: 'Sortierung' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
        </Form>
      </div>
    );
  };

  return (
    <div className='competition-section'>
      <div className='section-header'>
        <div>
          <div className='section-title'>Kampfrichter</div>
          <div className='section-subtitle'>Offizielle und ihre Funktionen</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Button
            icon='upload'
            text='CSV Import'
            stylingMode='contained'
            type='default'
            onClick={() => setImportPopupVisible(true)}
            disabled={!accessToken}
          />
          <div className='section-pill subtle'>{storeItems.length} Kampfrichter</div>
        </div>
      </div>

      <SplitPanel
        storageKey='splitPanel.officials'
        defaultLeft={340}
        minLeft={160}
        minRight={200}
        left={<OfficialsLeftPane ref={leftPaneRef} dataSource={dataSource} onSelect={handleSelect} onSaving={handleGridSaving} isSaving={isSaving} sessionLookup={sessionLookup} canEdit={canEdit} />}
        right={renderDetail()}
      />

      <Popup
        visible={importPopupVisible}
        onHiding={handleImportPopupClose}
        showTitle
        title='Kampfrichter per CSV importieren'
        width={720}
        height={620}
        dragEnabled={false}
      >
        <ScrollView style={{ height: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <div style={{ fontWeight: 600 }}>CSV auswählen</div>
              <div style={{ color: '#666', marginBottom: 8 }}>
                Erwartete Spalten: Position; Name; Verein; Lizenz; Wunsch. Abschnitte werden mit Zeilen wie "Abschnitt 1;" erkannt.
              </div>
              <FileUploader
                selectButtonText='Datei auswählen'
                labelText='oder Datei hier ablegen'
                accept='.csv,.txt'
                uploadMode='useForm'
                showFileList={false}
                onValueChanged={handleCsvSelected}
              />
              {importFileName && <div style={{ marginTop: 8 }}>Aktuelle Datei: {importFileName}</div>}
            </div>

            {analysis && (
              <div>
                <div style={{ fontWeight: 600 }}>Analyse</div>
                <div style={{ color: '#666', marginBottom: 8 }}>
                  Gefundene Abschnitte: {(analysis.sectionNumbers || []).join(', ')} — Gesamt {analysis.totalEntries || 0} Eintraege.
                </div>
                {analysis.missingSectionNumbers && analysis.missingSectionNumbers.length > 0 && (
                  <div style={{ color: '#b00', marginBottom: 8 }}>
                    Keine passenden Sessions im Wettkampf fuer Abschnitt(e): {analysis.missingSectionNumbers.join(', ')}.
                  </div>
                )}
                {analysis.warnings && analysis.warnings.length > 0 && (
                  <div style={{ color: '#cc7a00', marginBottom: 8 }}>
                    Warnungen:
                    <ul style={{ paddingLeft: 18, margin: 4 }}>
                      {analysis.warnings.slice(0, 5).map((w, idx) => <li key={idx}>{w}</li>)}
                      {analysis.warnings.length > 5 && <li>... {analysis.warnings.length - 5} weitere</li>}
                    </ul>
                  </div>
                )}
                {analysis.preview && analysis.preview.length > 0 && (
                  <>
                    <div style={{ color: '#666' }}>Vorschau (erste 5 Zeilen):</div>
                    <ul style={{ paddingLeft: 18, margin: 4 }}>
                      {analysis.preview.slice(0, 5).map((entry, idx) => (
                        <li key={idx}>
                          Abschnitt {entry.sessionNumber} ({entry.sessionLabel || sessionLabelByNumber.get(entry.sessionNumber) || 'unbekannt'}): {entry.officialTitle} - {entry.officialName} ({entry.officeClub || 'kein Verein'})
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}

            <div>
              <CheckBox
                text='Bestehende Kampfrichter in den gefundenen Abschnitten vor dem Import löschen'
                value={replaceExisting}
                onValueChanged={(e) => setReplaceExisting(e.value)}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button text='Abbrechen' onClick={handleImportPopupClose} />
              <Button
                text='Import starten'
                type='success'
                disabled={!analysis || !analysis.success || (analysis.missingSectionNumbers && analysis.missingSectionNumbers.length > 0)}
                onClick={handleImportCsv}
              />
            </div>
          </div>
        </ScrollView>
      </Popup>

      <LoadPanel
        visible={isImporting}
        message={importLoadingText || 'Import läuft...'}
        shading
        showIndicator
        showPane
      />
    </div>
  );
}
